import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { SelectableLLMModel } from "@/lib/selectableLLMModels";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/alertDialog";
import { Loader2 } from "lucide-react";
import type { FormEvent } from "react";

import { CREATE_WITH_AGENT_COPY } from "./createWithAgentCopy";
import type { CreateWithAgentCreatedOrder, CreateWithAgentView } from "./createWithAgentTypes";
import { CreateWithAgentHeader } from "./CreateWithAgentHeader";
import { planningSessionPhase } from "./planningSessionActivity";
import { PlanningSessionSurveyForm } from "./PlanningSessionSurveyForm";
import { JumpToLatestPill } from "./work-order-split-run/JumpToLatestPill";
import { PhaseLogCard } from "./work-order-split-run/PhaseLogCard";
import { SplitRunAttentionNote } from "./work-order-split-run/SplitRunAttentionNote";
import { WorkOrderSplitRunDescription } from "./work-order-split-run/WorkOrderSplitRunDescription";
import { useFollowLogScroll } from "./work-order-split-run/useFollowLogScroll";

export type CreateWithAgentDialogProps = {
  open: boolean;
  workspaceName: string;
  organizationId?: string;
  factoryId?: string;
  view: CreateWithAgentView;
  onComposerChange: (value: string) => void;
  onSend: () => void;
  onSubmitSurvey: (text: string) => void;
  onDraftTitleChange: (title: string) => void;
  onCreateDraft: () => void;
  onSkipDraft: () => void;
  onSelectCreated: (order: CreateWithAgentCreatedOrder) => void;
  onRefineCreated: (order: CreateWithAgentCreatedOrder) => void;
  onRequestClose: () => void;
  onCancelEnd: () => void;
  onConfirmEnd: () => void;
  onSelectModel?: (key: string) => void;
  modelPickerOpen?: boolean;
  onModelPickerOpenChange?: (open: boolean) => void;
  models?: SelectableLLMModel[];
};

export function CreateWithAgentDialog({
  open,
  workspaceName,
  organizationId = "",
  factoryId = "",
  view,
  onComposerChange,
  onSend,
  onSubmitSurvey,
  onDraftTitleChange,
  onCreateDraft,
  onSkipDraft,
  onSelectCreated,
  onRefineCreated,
  onRequestClose,
  onCancelEnd,
  onConfirmEnd,
  onSelectModel,
  modelPickerOpen,
  onModelPickerOpenChange,
  models,
}: CreateWithAgentDialogProps) {
  return (
    <>
      <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? undefined : onRequestClose())}>
        <DialogContent
          showCloseButton={false}
          size="large"
          className="flex h-[min(90vh,720px)] w-[calc(100%-2rem)] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:rounded-xl"
          data-testid="create-with-agent-dialog"
        >
          <CreateWithAgentHeader
            workspaceName={workspaceName}
            organizationId={organizationId}
            factoryId={factoryId}
            repository={view.repository}
            machineStatus={view.machineStatus}
            selectableModelKey={view.selectableModelKey}
            refining={view.refining}
            onEndSession={onRequestClose}
            onSelectModel={onSelectModel}
            modelPickerOpen={modelPickerOpen}
            onModelPickerOpenChange={onModelPickerOpenChange}
            models={models}
          />
          <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-2">
            <CreateWithAgentStream
              organizationId={organizationId}
              view={view}
              onComposerChange={onComposerChange}
              onSend={onSend}
              onSubmitSurvey={onSubmitSurvey}
            />
            <CreateWithAgentWorkPane
              view={view}
              failed={view.machineStatus === "failed"}
              onDraftTitleChange={onDraftTitleChange}
              onCreateDraft={onCreateDraft}
              onSkipDraft={onSkipDraft}
              onSelectCreated={onSelectCreated}
              onRefineCreated={onRefineCreated}
            />
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={view.endConfirmOpen} onOpenChange={(nextOpen) => (nextOpen ? undefined : onCancelEnd())}>
        <AlertDialogContent data-testid="create-with-agent-end-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{CREATE_WITH_AGENT_COPY.endSessionAsk}</AlertDialogTitle>
            <AlertDialogDescription>{CREATE_WITH_AGENT_COPY.endSessionBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onCancelEnd}>{CREATE_WITH_AGENT_COPY.keepSession}</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmEnd}>{CREATE_WITH_AGENT_COPY.endSessionConfirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function CreateWithAgentStream({
  organizationId,
  view,
  onComposerChange,
  onSend,
  onSubmitSurvey,
}: {
  organizationId: string;
  view: CreateWithAgentView;
  onComposerChange: (value: string) => void;
  onSend: () => void;
  onSubmitSurvey: (text: string) => void;
}) {
  const follow = useFollowLogScroll<HTMLDivElement>(
    view.executionId || view.canvasId || "session",
    view.messages.length,
    {
      resumeOnBottom: true,
    },
  );
  const failed = view.machineStatus === "failed";
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (failed) {
      return;
    }
    onSend();
  };

  return (
    <section
      className="flex min-h-0 flex-col border-b border-border bg-muted/25 md:border-r md:border-b-0"
      data-testid="create-with-agent-stream"
    >
      <div className="relative min-h-0 flex-1">
        <div
          ref={follow.scrollRef}
          onScroll={follow.onScroll}
          className="absolute inset-0 overflow-y-auto px-3 py-3"
          data-testid="create-with-agent-log"
        >
          {showCreateWithAgentLogIntro(view) ? (
            <CreateWithAgentLogIntro />
          ) : (
            <PhaseLogCard
              phase={planningSessionPhase(view)}
              expanded
              collapsible={false}
              organizationId={organizationId}
              canvasId={view.canvasId}
              compactSessionLog
            />
          )}
        </div>
        {follow.following ? null : (
          <JumpToLatestPill onJumpToLatest={() => follow.setFollowing(true)} testId="create-with-agent-older" />
        )}
      </div>
      {failed ? (
        <SplitRunAttentionNote
          tone="failed"
          note={{
            headline: CREATE_WITH_AGENT_COPY.machineStopped,
            text: CREATE_WITH_AGENT_COPY.machineFailedBody,
          }}
        />
      ) : null}
      {view.survey && !failed ? (
        <PlanningSessionSurveyForm
          key={view.survey.id ?? view.survey.questions[0]?.prompt ?? "survey"}
          survey={view.survey}
          onSubmit={onSubmitSurvey}
        />
      ) : null}
      <form className="border-t border-border bg-background p-3" onSubmit={handleSubmit}>
        <label htmlFor="create-with-agent-composer" className="sr-only">
          {view.refining
            ? CREATE_WITH_AGENT_COPY.composerPlaceholderRefine
            : CREATE_WITH_AGENT_COPY.composerPlaceholder}
        </label>
        <div className="flex items-end gap-2">
          <Textarea
            id="create-with-agent-composer"
            data-testid="create-with-agent-composer"
            value={view.composer}
            placeholder={
              view.refining
                ? CREATE_WITH_AGENT_COPY.composerPlaceholderRefine
                : CREATE_WITH_AGENT_COPY.composerPlaceholder
            }
            disabled={failed}
            onChange={(event) => onComposerChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (!failed) {
                  onSend();
                }
              }
            }}
            className="min-h-[44px] resize-none text-[13px]"
            rows={2}
          />
          <Button type="submit" size="sm" disabled={failed || !view.composer.trim()}>
            {CREATE_WITH_AGENT_COPY.send}
          </Button>
        </div>
      </form>
    </section>
  );
}

function showCreateWithAgentLogIntro(view: CreateWithAgentView): boolean {
  return view.machineStatus === "starting" && view.messages.length === 0;
}

function CreateWithAgentLogIntro() {
  return (
    <div
      className="flex items-center gap-2 px-2 py-1.5 text-[13px] text-muted-foreground"
      data-testid="create-with-agent-log-intro"
    >
      <Loader2 className="size-3.5 animate-spin" aria-hidden />
      <p>{CREATE_WITH_AGENT_COPY.logStarting}</p>
    </div>
  );
}

function CreateWithAgentWorkPane({
  view,
  failed,
  onDraftTitleChange,
  onCreateDraft,
  onSkipDraft,
  onSelectCreated,
  onRefineCreated,
}: {
  view: CreateWithAgentView;
  failed: boolean;
  onDraftTitleChange: (title: string) => void;
  onCreateDraft: () => void;
  onSkipDraft: () => void;
  onSelectCreated: (order: CreateWithAgentCreatedOrder) => void;
  onRefineCreated: (order: CreateWithAgentCreatedOrder) => void;
}) {
  return (
    <section className="flex min-h-0 flex-col bg-muted/20" data-testid="create-with-agent-work">
      {view.created.length > 0 ? (
        <CreatedTaskList created={view.created} failed={failed} onSelect={onSelectCreated} onRefine={onRefineCreated} />
      ) : null}
      {view.right.kind === "empty" ? <EmptyWorkPane /> : null}
      {view.right.kind === "draft" ? (
        <DraftWorkPane
          title={view.right.draft.title}
          description={view.right.draft.description}
          failed={failed}
          refining={view.refining}
          onTitleChange={onDraftTitleChange}
          onCreate={onCreateDraft}
          onSkip={onSkipDraft}
        />
      ) : null}
      {view.right.kind === "preview" ? (
        <PreviewWorkPane order={view.right.order} failed={failed} onRefine={onRefineCreated} />
      ) : null}
    </section>
  );
}

function CreatedTaskList({
  created,
  failed,
  onSelect,
  onRefine,
}: {
  created: CreateWithAgentCreatedOrder[];
  failed: boolean;
  onSelect: (order: CreateWithAgentCreatedOrder) => void;
  onRefine: (order: CreateWithAgentCreatedOrder) => void;
}) {
  return (
    <div className="border-b border-border px-5 py-3" data-testid="create-with-agent-created">
      <p className="text-[12px] font-medium text-muted-foreground">{CREATE_WITH_AGENT_COPY.sessionList}</p>
      <ul className="mt-2 space-y-1.5">
        {created.map((order) => (
          <li key={order.id} className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="min-w-0 truncate text-left text-[13px] text-foreground hover:underline"
              onClick={() => onSelect(order)}
            >
              {order.key} {order.title}
            </button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 px-2 text-[12px]"
              disabled={failed}
              onClick={() => onRefine(order)}
            >
              {CREATE_WITH_AGENT_COPY.refineFurther}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyWorkPane() {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-8 text-center"
      data-testid="create-with-agent-empty"
    >
      <p className="text-[15px] font-medium tracking-[-0.01em] text-foreground">
        {CREATE_WITH_AGENT_COPY.emptyHeadline}
      </p>
      <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">{CREATE_WITH_AGENT_COPY.emptyBody}</p>
    </div>
  );
}

function DraftWorkPane({
  title,
  description,
  failed,
  refining,
  onTitleChange,
  onCreate,
  onSkip,
}: {
  title: string;
  description: string;
  failed: boolean;
  refining: boolean;
  onTitleChange: (title: string) => void;
  onCreate: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col px-5 py-4" data-testid="create-with-agent-draft">
      <p className="text-[12px] font-medium text-muted-foreground">{CREATE_WITH_AGENT_COPY.draftLabel}</p>
      <Textarea
        value={title}
        onChange={(event) => onTitleChange(event.target.value)}
        disabled={failed}
        rows={1}
        aria-label="Task title"
        data-testid="create-with-agent-draft-title"
        className="mt-3 min-h-0 resize-none border-0 bg-transparent p-0 text-[22px] font-semibold tracking-[-0.02em] shadow-none focus-visible:ring-0"
      />
      <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
        <WorkOrderSplitRunDescription description={description} collapsible={false} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" disabled={failed} onClick={onSkip}>
          {CREATE_WITH_AGENT_COPY.skip}
        </Button>
        <Button
          type="button"
          data-testid="create-with-agent-create"
          disabled={failed || !title.trim()}
          onClick={onCreate}
        >
          {refining ? CREATE_WITH_AGENT_COPY.update : CREATE_WITH_AGENT_COPY.create}
        </Button>
      </div>
    </div>
  );
}

function PreviewWorkPane({
  order,
  failed,
  onRefine,
}: {
  order: CreateWithAgentCreatedOrder;
  failed: boolean;
  onRefine: (order: CreateWithAgentCreatedOrder) => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col px-5 py-4" data-testid="create-with-agent-preview">
      <p className="text-[12px] font-medium text-muted-foreground">{order.key}</p>
      <h2 className="mt-2 text-[22px] font-semibold tracking-[-0.02em]">{order.title}</h2>
      <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
        <WorkOrderSplitRunDescription description={order.description} collapsible={false} />
      </div>
      <div className="mt-4 flex justify-end">
        <Button type="button" variant="outline" disabled={failed} onClick={() => onRefine(order)}>
          {CREATE_WITH_AGENT_COPY.refineFurther}
        </Button>
      </div>
    </div>
  );
}
